/**
 * The app's single TanStack Query client (moved out of main.tsx in quick-261021-rcw so
 * the `_app` route guard can drop the previous owner's in-memory queries on an account
 * switch without importing the entry module).
 *
 * Also wires the owner-scoped device cache (lib/deviceQueryCache.ts): successful
 * `['categories']` / `['preferences']` results are persisted so a cold start — including
 * the iOS "Add entry" quick action — can render the add form from the last answer while
 * the network revalidates in the background.
 *
 * Lib code never imports the router: the auth-expiry redirect is injected from main.tsx
 * via setOnAuthExpired.
 */
import { QueryClient, QueryCache } from '@tanstack/react-query';
import { Hub } from 'aws-amplify/utils';
import { AuthSessionExpiredError } from '@/lib/api';
import { useImpersonationStore } from '@/features/admin/impersonationStore';
import { isRunningInNativeShell } from '@/lib/pwa';
import {
  attachDeviceCachePersistence,
  clearDeviceQueryCache,
  isAuthOrCacheStorageKey,
  readCognitoSubFromStorage,
  resyncCacheOwnerFromTokenStore,
  setCacheOwner,
  setOwnerVerifier,
} from '@/lib/deviceQueryCache';

let onAuthExpired: (() => void) | null = null;

/** Registers what to do when a query fails because the Cognito session is gone. */
export function setOnAuthExpired(cb: () => void): void {
  onAuthExpired = cb;
}

export const queryClient = new QueryClient({
  // When a query fails because the Cognito session expired/was invalid (e.g. stale
  // tokens for a deleted user — see debug session google-sso-budget-401), api.ts
  // has already cleared the broken session. Route the user back to sign-in instead
  // of leaving them on a half-authenticated shell showing a generic data error.
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AuthSessionExpiredError) {
        onAuthExpired?.();
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      // Do not retry auth-expiry failures — the session is gone, retrying is futile.
      retry: (failureCount, error) =>
        error instanceof AuthSessionExpiredError ? false : failureCount < 1,
    },
  },
});

// Persist categories/preferences for the current owner. Never while an admin is in
// "View as" (threat T-rcw-02) — the target's data must not be stored under the admin.
attachDeviceCachePersistence(queryClient, () => useImpersonationStore.getState().targetSub);

// Sign-out wipes the persisted copy immediately and un-scopes the cache, so a late
// response cannot re-seed it. The owner check in routes/_app.tsx beforeLoad remains the
// primary guard: native Google sign-in emits no Hub `signedIn`, and the next beforeLoad
// re-scopes the cache (and drops in-memory queries) for whoever comes next.
Hub.listen('auth', ({ payload }) => {
  if (payload.event === 'signedOut') {
    setCacheOwner(null);
    clearDeviceQueryCache();
  }
});

/**
 * Bumped every time the in-memory queries are dropped for a new owner. The `_app` layout
 * is keyed on it (review WR-02): removeQueries() alone leaves mounted observers holding
 * the removed Query objects — on iOS sign-out and session expiry the layout stays mounted,
 * so the calendar would keep showing the previous owner's data — and remounting makes
 * every observer start from the new owner's cache.
 */
let ownerGeneration = 0;
const ownerGenerationListeners = new Set<() => void>();

export function getOwnerGeneration(): number {
  return ownerGeneration;
}

export function subscribeOwnerGeneration(listener: () => void): () => void {
  ownerGenerationListeners.add(listener);
  return () => {
    ownerGenerationListeners.delete(listener);
  };
}

/**
 * Drops every in-memory query and remounts the app subtree. Called when the cache owner
 * changes, because all of them belong to the previous owner.
 */
export function dropQueriesForNewOwner(): void {
  queryClient.removeQueries();
  ownerGeneration += 1;
  ownerGenerationListeners.forEach((listener) => listener());
}

// Multiple web tabs (review CR-01). Amplify's web token store is shared localStorage, so
// another tab can sign out or sign in as someone else while this tab still believes the
// old owner. Every cache read/write first checks the sub the token store holds now, and a
// `storage` event for a Cognito key or the cache key re-derives this tab's owner and drops
// its in-memory queries. Native has one WebView and keeps tokens in secure storage, so
// neither applies there.
if (typeof window !== 'undefined' && !isRunningInNativeShell()) {
  const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID as string;
  setOwnerVerifier(() => readCognitoSubFromStorage(clientId));
  window.addEventListener('storage', (e) => {
    if (isAuthOrCacheStorageKey(e.key) && resyncCacheOwnerFromTokenStore()) {
      dropQueriesForNewOwner();
    }
  });
}
